import { Grid } from "@mui/material";
import { Dispatch, SetStateAction, memo, useMemo } from "react";
import { FilledTextArea } from "../../../../components/FilledTextArea";
import { titleCase } from "../../../../utils";
import { TVerificationAppFieldConfigItem } from "../../../app/models";
import { FieldSet } from "../../../ui/components/FieldSet";
import { TDocumentData, TDocumentInfo } from "../../models";
import { ConfigContentField } from "./ConfigContentField";

export type Props = {
  data?: TDocumentData | null;
  /**
   * Legacy local-draft setter. The AccordionShell RFF form is now the source
   * of truth for editable payload state, so callers no longer need to wire
   * setDataFields; kept for backward compatibility with read-only renderers
   * that still pass it.
   */
  setDataFields?: Dispatch<SetStateAction<object>>;
  /**
   * Legacy local-draft snapshot. Display-only formatters (TaxId,
   * boolean Yes/No text) read from this when present; otherwise they fall
   * back to the loaded payload from `data`.
   */
  dataFields?: object;
  allDisabled?: boolean;
  configFields?: TVerificationAppFieldConfigItem[];
  fileItems?: TDocumentData[];
  requestId?: string;
  documentInfo?: TDocumentInfo;
};

export const BinaryContent = memo((props: Props) => {
  const {
    data,
    allDisabled,
    configFields,
    fileItems,
    requestId,
    documentInfo,
  } = props;

  // The display-only formatters (TaxId, boolean text fallback) read
  // their value from this snapshot. Editable text fields ignore it because
  // mui-rff binds them to RFF state on the parent AccordionShell form.
  const formData = useMemo(
    () => (data?.data ?? {}) as Record<string, unknown>,
    [data],
  );

  const hasConfigFields = !!(configFields && configFields.length > 0);

  const fallbackKeys = useMemo(() => {
    if (hasConfigFields) return [];
    return Object.keys(formData);
  }, [formData, hasConfigFields]);

  return (
    <Grid sx={{ marginBottom: 4 }} direction="column" container>
      <FieldSet>
        {hasConfigFields &&
          configFields!.map((field, index) => (
            <ConfigContentField
              key={`field-${field.name ?? index}`}
              allDisabled={allDisabled}
              fields={formData}
              field={field}
              fileItems={fileItems}
              requestId={requestId}
              documentInfo={documentInfo}
            />
          ))}
        {fallbackKeys.map((key) =>
          allDisabled ? (
            <FilledTextArea
              key={key}
              value={(formData as any)[key]}
              name={key}
              label={titleCase(key)}
              inputProps={{ disabled: true }}
            />
          ) : (
            <FilledTextArea key={key} name={key} label={titleCase(key)} />
          ),
        )}
      </FieldSet>
    </Grid>
  );
});
